export type SelectionSnapshot = {
  workbookName: string;
  sheetName: string;
  address: string;
  rowCount: number;
  columnCount: number;
  values: unknown[][];
};

export class NoWorkbookError extends Error {
  constructor() {
    super("No workbook is open.");
    this.name = "NoWorkbookError";
  }
}

export class NoSelectionError extends Error {
  constructor() {
    super("Nothing is selected. Click a cell first.");
    this.name = "NoSelectionError";
  }
}

export function whenOfficeReady(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof Office === "undefined") {
      resolve();
      return;
    }
    Office.onReady(() => resolve());
  });
}

export async function readSelection(): Promise<SelectionSnapshot> {
  try {
    return await Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      const sheet = wb.worksheets.getActiveWorksheet();
      const r = wb.getSelectedRange();
      wb.load(["name"]);
      sheet.load(["name"]);
      r.load(["address", "values", "rowCount", "columnCount"]);
      await ctx.sync();
      return {
        workbookName: wb.name,
        sheetName: sheet.name,
        address: r.address,
        rowCount: r.rowCount,
        columnCount: r.columnCount,
        values: r.values,
      };
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : "";
    // Office.js doesn't expose distinct error codes for "no workbook" vs
    // "no selection"; classify by message keywords. Fall back to NoSelectionError
    // since "nothing selected" is the more common cause inside an open workbook.
    if (/workbook|no document|requires a workbook/i.test(msg)) {
      throw new NoWorkbookError();
    }
    throw new NoSelectionError();
  }
}

export async function readActiveWorkbookName(): Promise<string> {
  return Excel.run(async (ctx) => {
    ctx.workbook.load(["name"]);
    await ctx.sync();
    return ctx.workbook.name;
  });
}

// =============================================================================
// Action execution helpers (Phase 2)
// =============================================================================

/**
 * Set the value (or formula) of a single cell. Address is in A1 notation,
 * optionally prefixed with the sheet name (e.g., "Sheet1!D5").
 */
export async function insertIntoCell(address: string, value: string | number): Promise<void> {
  await Excel.run(async (ctx) => {
    const range = parseSheetAddress(ctx, address);
    range.values = [[value]];
    await ctx.sync();
  });
}

/**
 * Fill a 2D range. `values` rows must equal range rows; values columns must equal range columns.
 */
export async function insertIntoRange(address: string, values: unknown[][]): Promise<void> {
  await Excel.run(async (ctx) => {
    const range = parseSheetAddress(ctx, address);
    range.values = values as any;
    await ctx.sync();
  });
}

/**
 * Insert a new column to the right of `afterColumn` (e.g., "D"), with a header in row 1
 * and a formula filled down through the data rows of the existing data region.
 */
export async function addColumn(opts: {
  afterColumn: string;
  header: string;
  formula: string;
}): Promise<void> {
  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
    const usedRange = sheet.getUsedRange();
    usedRange.load(["rowCount"]);
    await ctx.sync();
    const rows = usedRange.rowCount;

    const insertColLetter = nextColumnLetter(opts.afterColumn);
    const headerCell = sheet.getRange(`${insertColLetter}1`);
    headerCell.insert(Excel.InsertShiftDirection.right);
    headerCell.values = [[opts.header]];

    if (rows > 1) {
      const formulaRange = sheet.getRange(`${insertColLetter}2:${insertColLetter}${rows}`);
      const formulas: string[][] = [];
      for (let r = 2; r <= rows; r++) {
        formulas.push([opts.formula.replaceAll("{row}", String(r))]);
      }
      formulaRange.formulas = formulas;
    }
    await ctx.sync();
  });
}

/**
 * Add a new sheet with the given name and write `content` (rows of values) starting at A1.
 */
export async function addSheet(opts: { name: string; content: unknown[][] }): Promise<void> {
  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.add(opts.name);
    if (opts.content.length > 0) {
      const cols = Math.max(...opts.content.map((r) => r.length));
      const target = sheet.getRangeByIndexes(0, 0, opts.content.length, cols);
      // pad rows to be uniform width
      const padded = opts.content.map((r) =>
        r.length === cols ? r : [...r, ...Array(cols - r.length).fill("")],
      );
      target.values = padded as any;
    }
    sheet.activate();
    await ctx.sync();
  });
}

/**
 * Navigate to + select the given range. Used for `select_range` action.
 */
export async function selectRange(address: string): Promise<void> {
  await Excel.run(async (ctx) => {
    const range = parseSheetAddress(ctx, address);
    range.select();
    await ctx.sync();
  });
}

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

function parseSheetAddress(ctx: Excel.RequestContext, address: string): Excel.Range {
  const bangIdx = address.indexOf("!");
  if (bangIdx >= 0) {
    const sheetName = address.slice(0, bangIdx);
    const rangeAddr = address.slice(bangIdx + 1);
    return ctx.workbook.worksheets.getItem(sheetName).getRange(rangeAddr);
  }
  return ctx.workbook.worksheets.getActiveWorksheet().getRange(address);
}

function nextColumnLetter(col: string): string {
  const upper = col.toUpperCase();
  if (upper.length !== 1) {
    // Multi-letter columns (AA, AB, …) — supportable but not v1.
    throw new Error(`addColumn only supports single-letter columns in v1, got: ${col}`);
  }
  const code = upper.charCodeAt(0) - 64;
  if (code < 1 || code >= 26) {
    throw new Error(`addColumn: column letter must be A-Y, got: ${col}`);
  }
  return String.fromCharCode(64 + code + 1);
}

/**
 * Reads Office's host-app theme indicator and classifies it as "dark" / "light" / "unknown".
 *
 * Office.context.officeTheme.bodyBackgroundColor is the documented signal: hex string
 * representing the host's body chrome color. We compute relative luminance and threshold
 * at 128 (rec601 weighted, simple but adequate for this binary classification).
 *
 * Used to fire the `excel.host_theme_mismatch` PostHog event when the host is light
 * (we ship dark-only in v1; this lets us quantify whether to prioritize light-mode parity).
 */
export function readHostTheme(): "dark" | "light" | "unknown" {
  if (typeof Office === "undefined" || !Office.context?.officeTheme) return "unknown";
  const bg = Office.context.officeTheme.bodyBackgroundColor ?? "";
  if (!bg) return "unknown";
  const hex = bg.replace("#", "");
  if (hex.length !== 6) return "unknown";
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return "unknown";
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 128 ? "light" : "dark";
}
